/**
 * Hook for managing menu scope content generation
 * Handles text keys and labels based on user state and organization access
 *
 * @module hooks/menuScope/useMenuScopeContent
 *
 * @param {boolean} isNewUser - Whether user is new
 * @param {boolean | null} hasOrganizationAccess - Whether user has organization access (null = not initialized)
 * @param {string|undefined} storeName - Current store name
 *
 * @returns {Object} Content information
 * @returns {Object} returns.textKeys - Text keys for translations
 * @returns {string} returns.sucursalLabel - Label for store/branch card
 *
 * @example
 * const { textKeys, sucursalLabel } = useMenuScopeContent(isNewUser, hasOrganizationAccess, storeName);
 */

import { useMemo } from 'react';
import { getMenuScopeTextKeys, getSucursalLabel } from 'utils/menuMaker/menuScopeTexts';

interface UseMenuScopeContentParams {
  isNewUser: boolean;
  hasOrganizationAccess: boolean | null;
  storeName?: string;
}

interface UseMenuScopeContentReturn {
  textKeys: ReturnType<typeof getMenuScopeTextKeys>;
  sucursalLabel: string;
}

export const useMenuScopeContent = ({
  isNewUser,
  hasOrganizationAccess,
  storeName,
}: UseMenuScopeContentParams): UseMenuScopeContentReturn => {
  // Get appropriate text keys based on user state (memoized for performance)
  // Treat null hasOrganizationAccess as false for compatibility
  const textKeys = useMemo(() => getMenuScopeTextKeys(isNewUser, hasOrganizationAccess ?? false), [
    isNewUser,
    hasOrganizationAccess,
  ]);

  // Get sucursal label (store name for existing users, "Sucursal" for new users) (memoized for performance)
  const sucursalLabel = useMemo(() => getSucursalLabel(isNewUser, storeName), [isNewUser, storeName]);
  return {
    textKeys,
    sucursalLabel,
  };
};
